import { Box, Typography, useTheme } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ColorModeContext } from '../../global/theme/theme';
import { DocIcon } from './DocIcon';
import { formatEntryMeta } from './entryMeta';
import { labelForRole, sortRoles } from './roleLabels';

const sectionLabelSx = {
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: 'var(--text2)',
  padding: '10px 10px 6px',
};

const navButtonSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '9px 10px',
  border: 'none',
  background: 'transparent',
  color: 'var(--text)',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  borderRadius: '8px',
  cursor: 'pointer',
  textAlign: 'left',
  ':hover': { background: 'var(--panel2)' },
};

export const HelpSidebar = ({
  navOpen,
  onClose,
  searchRef,
  searchInput,
  onSearchChange,
  role,
  setRole,
  topics,
  entries,
  courses,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const location = useLocation();

  const activeArticle = location.pathname.match(
    /\/documentation\/articles\/([^/?]+)/
  )?.[1];
  const activeCourse = location.pathname.match(
    /\/documentation\/courses\/([^/?]+)/
  )?.[1];

  const [expanded, setExpanded] = useState({});
  const roleActive = role !== 'all';

  const groups = useMemo(() => {
    if (!topics || !entries) return null;
    return topics
      .map((topic) => ({
        ...topic,
        items: entries.filter(
          (entry) =>
            entry.topics?.some((item) => item.slug === topic.slug) &&
            (!roleActive || !entry.roles?.length || entry.roles.includes(role))
        ),
      }))
      .filter((topic) => topic.items.length > 0);
  }, [topics, entries, role, roleActive]);

  const availableRoles = useMemo(
    () => sortRoles([...new Set((entries || []).flatMap((e) => e.roles || []))]),
    [entries]
  );

  // Keep the topic of the open article expanded
  useEffect(() => {
    if (!activeArticle || !entries) return;
    const entry = entries.find((item) => item.slug === activeArticle);
    if (!entry?.topics) return;
    setExpanded((prev) => {
      const next = { ...prev };
      entry.topics.forEach((topic) => (next[topic.slug] = true));
      return next;
    });
  }, [activeArticle, entries]);

  function openEntry(slug) {
    navigate(`/documentation/articles/${slug}`);
    onClose();
  }

  return (
    <Box
      component="nav"
      className="flex-none h-full flex flex-col z-40 max-md:fixed max-md:left-0 max-md:top-0 max-md:bottom-0 transition-transform duration-200"
      sx={{
        width: '296px',
        background: 'var(--panel)',
        borderRight: '1px solid var(--border)',
        // 768px matches Tailwind's md (the max-md:fixed above) — MUI's md is
        // 900px, which would leave a 768–900px window with the sidebar in the
        // layout flow but translated off-screen
        transform: navOpen ? 'none' : 'translateX(-100%)',
        '@media (min-width: 768px)': { transform: 'none' },
      }}
    >
      <Box sx={{ padding: '20px 20px 14px' }} className="flex items-center justify-between">
        <Box
          className="cursor-pointer"
          sx={{ fontSize: '24px', fontWeight: 600, letterSpacing: '.01em' }}
          onClick={() => {
            navigate('/documentation');
            onClose();
          }}
        >
          <Box component="span" sx={{ color: 'var(--accent)' }}>
            Exo
          </Box>
          <span>Paper</span>
          <Box
            component="span"
            sx={{
              color: 'var(--text2)',
              fontWeight: 500,
              fontSize: '13px',
              marginLeft: '8px',
            }}
          >
            {t('Help')}
          </Box>
        </Box>
        <Box
          component="button"
          title={t('Switch theme')}
          onClick={colorMode.toggleColorMode}
          className="inline-flex items-center justify-center cursor-pointer"
          sx={{
            border: '1px solid var(--border)',
            background: 'var(--panel2)',
            color: 'var(--text2)',
            borderRadius: '8px',
            width: '32px',
            height: '32px',
          }}
        >
          <DocIcon
            name={theme.palette.mode === 'dark' ? 'light_mode' : 'dark_mode'}
            size={18}
          />
        </Box>
      </Box>

      <Box sx={{ padding: '0 16px 14px' }}>
        <Box
          component="label"
          className="flex items-center gap-2 cursor-text"
          sx={{
            background: 'var(--panel2)',
            border: '1px solid var(--border)',
            borderRadius: '10px',
            padding: '9px 12px',
          }}
        >
          <DocIcon name="search" size={18} color="var(--text2)" />
          <Box
            component="input"
            ref={searchRef}
            placeholder={t('Search the help …')}
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            className="flex-1 min-w-0 border-none outline-none bg-transparent"
            sx={{ color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px' }}
          />
          <Box
            component="kbd"
            sx={{
              fontFamily: 'inherit',
              fontSize: '10px',
              fontWeight: 600,
              color: 'var(--text2)',
              border: '1px solid var(--border)',
              borderRadius: '5px',
              padding: '2px 6px',
              background: 'var(--panel)',
            }}
          >
            ⌘ K
          </Box>
        </Box>
        <Box className="flex items-center gap-2 mt-2">
          <DocIcon name="badge" size={16} color="var(--text2)" />
          <Box
            component="select"
            value={role}
            title={t('Show only entries for this role')}
            onChange={(e) => setRole(e.target.value)}
            className="flex-1 min-w-0 cursor-pointer outline-none appearance-none"
            sx={{
              border: '1px solid var(--border)',
              background: 'var(--panel2)',
              color: 'var(--text)',
              fontFamily: 'inherit',
              fontSize: '12px',
              fontWeight: 500,
              borderRadius: '8px',
              padding: '7px 10px',
            }}
          >
            <option value="all">{t('All roles')}</option>
            {availableRoles.map((item) => (
              <option key={item} value={item}>
                {labelForRole(item)}
              </option>
            ))}
          </Box>
          {roleActive && (
            <Box
              component="button"
              title={t('Reset filter')}
              onClick={() => setRole('all')}
              className="inline-flex flex-none items-center justify-center cursor-pointer"
              sx={{
                border: '1px solid var(--border)',
                background: 'var(--panel2)',
                color: 'var(--text2)',
                borderRadius: '8px',
                width: '30px',
                height: '30px',
              }}
            >
              <DocIcon name="filter_alt_off" size={16} />
            </Box>
          )}
        </Box>
        {roleActive && (
          <Box
            sx={{ fontSize: '10.5px', color: 'var(--text2)', marginTop: '6px', paddingLeft: '24px' }}
          >
            {t('Only entries for “{{role}}” are shown.', {
              role: labelForRole(role),
            })}
          </Box>
        )}
      </Box>

      <Box className="flex-1 overflow-y-auto" sx={{ padding: '0 12px 24px' }}>
        <Typography sx={sectionLabelSx}>{t('Topics')}</Typography>
        {groups?.map((topic) => {
          const isExpanded = roleActive || !!expanded[topic.slug];
          return (
            <Box key={topic.slug} sx={{ marginBottom: '2px' }}>
              <Box
                component="button"
                sx={navButtonSx}
                onClick={() =>
                  setExpanded((prev) => ({
                    ...prev,
                    [topic.slug]: !prev[topic.slug],
                  }))
                }
              >
                <DocIcon name={topic.icon} size={19} color="var(--text2)" />
                <Box component="span" className="flex-1">
                  {topic.name}
                </Box>
                <DocIcon
                  name={isExpanded ? 'expand_less' : 'expand_more'}
                  size={18}
                  color="var(--text2)"
                />
              </Box>
              {isExpanded && (
                <Box className="flex flex-col gap-px" sx={{ padding: '2px 0 6px 14px' }}>
                  {topic.items.map((entry) => {
                    const active = entry.slug === activeArticle;
                    return (
                      <Box
                        key={entry.slug}
                        component="button"
                        onClick={() => openEntry(entry.slug)}
                        className="flex items-center cursor-pointer text-left"
                        sx={{
                          gap: '9px',
                          padding: '7px 10px',
                          border: `1px solid ${active ? 'var(--accent-border)' : 'transparent'}`,
                          background: active ? 'var(--accent-soft)' : 'transparent',
                          color: 'var(--text)',
                          fontFamily: 'inherit',
                          fontSize: '12.5px',
                          fontWeight: 500,
                          borderRadius: '8px',
                          ':hover': { background: 'var(--panel2)' },
                        }}
                      >
                        <DocIcon
                          name={
                            entry.contentType === 'video'
                              ? 'play_circle'
                              : entry.icon
                          }
                          size={17}
                          color={active ? 'var(--accent-strong)' : 'var(--text2)'}
                        />
                        <Box component="span" className="flex-1">
                          {entry.title}
                        </Box>
                        <Box
                          component="span"
                          className="whitespace-nowrap"
                          sx={{ fontSize: '10.5px', color: 'var(--text2)' }}
                        >
                          {formatEntryMeta(entry, t)}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}

        {courses && courses.length > 0 && (
          <>
            <Typography
              sx={{
                ...sectionLabelSx,
                padding: '18px 10px 6px',
                borderTop: '1px solid var(--border)',
                marginTop: '14px',
              }}
            >
              {t('Courses')}
            </Typography>
            {courses.map((course) => {
              const active = course.slug === activeCourse;
              return (
                <Box
                  key={course.slug}
                  component="button"
                  onClick={() => {
                    navigate(`/documentation/courses/${course.slug}`);
                    onClose();
                  }}
                  className="w-full flex items-start cursor-pointer text-left"
                  sx={{
                    gap: '10px',
                    padding: '9px 10px',
                    border: `1px solid ${active ? 'var(--accent-border)' : 'transparent'}`,
                    background: active ? 'var(--accent-soft)' : 'transparent',
                    color: 'var(--text)',
                    fontFamily: 'inherit',
                    borderRadius: '8px',
                    marginBottom: '2px',
                    ':hover': { background: 'var(--panel2)' },
                  }}
                >
                  <DocIcon
                    name="school"
                    size={18}
                    color="var(--accent)"
                    className="mt-px"
                  />
                  <Box component="span" className="flex-1">
                    <Box
                      component="span"
                      className="block"
                      sx={{ fontSize: '12.5px', fontWeight: 600 }}
                    >
                      {course.title}
                    </Box>
                    <Box
                      component="span"
                      className="block"
                      sx={{ fontSize: '10.5px', color: 'var(--text2)', marginTop: '2px' }}
                    >
                      {t('{{lessons}} lessons · {{minutes}} min.', {
                        lessons: course.lessonsCount,
                        minutes: course.totalMinutes,
                      })}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </Box>
    </Box>
  );
};
